import React, { Fragment, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import I18n from 'i18n-js';
import { Button } from 'grab-ui';

import scribeTrack from 'scribe/scribe-track';
import instrumentationDuck from 'ducks/instrumentation-duck';
import { stopTrackingInterval } from 'actions/api-actions';
import { appDataSelector } from 'selectors/app-data-selector';
import useGuid from 'hooks/useGuid';
import useSearchParams from 'hooks/useSearchParams';
import {
  useAuthModule,
  useExperimentModule,
  usePaymentCheckoutModule,
} from 'hooks/native';
import fetchData from 'common/utils/fetch-data';
import { inGrabApp, isMobile } from 'common/utils/platform';
import {
  absAmount,
  formatPrice,
  isNegativeAmount,
} from 'common/utils/currency';
import { isCashlessOnly } from 'common/utils/payment';
import { FETCH_DATA_TYPE } from 'constants/fetch-data';
import { COD_PAYMENT_PENDING_STATUS } from 'constants/payment';
import { DROPPING_OFF, FAILED_DELIVERY } from 'constants/new-order-status';

import UpdateAppDrawer from '../drawer/update-app-drawer';
import styles from './pay-on-delivery-payment-footer.less';

const PayOnDeliveryPaymentFooter = ({
  status,
  codPaymentStatus,
  symbol,
  userType,
  payOnDelivery,
}) => {
  const dispatch = useDispatch();
  const guid = useGuid();
  const [searchParams] = useSearchParams();
  const { getAccessToken, inApp } = useAuthModule();
  const { proceedToPay } = usePaymentCheckoutModule();
  const { getBoolValue } = useExperimentModule();
  const { trackingInterval } = useSelector(appDataSelector);
  const { orderId, trackingPageType, trackingSource } = useSelector(
    instrumentationDuck.selectors.instrumentation,
  );
  const [disabled, setDisabled] = useState(false);
  const [versionUpdateVisible, setVersionUpdateVisible] = useState(false);

  if (!payOnDelivery) return null;

  const { isReceiver } = userType;
  const cashlessOnly =
    isReceiver &&
    (status === DROPPING_OFF || status === FAILED_DELIVERY) &&
    isCashlessOnly(payOnDelivery);
  const mixPayment =
    isReceiver &&
    (status === DROPPING_OFF || status === FAILED_DELIVERY) &&
    !!parseFloat(payOnDelivery.totalCashlessAmount) &&
    !!parseFloat(payOnDelivery.totalCashAmount);
  const showButton =
    inGrabApp() &&
    isMobile() &&
    isReceiver &&
    (status === DROPPING_OFF || status === FAILED_DELIVERY) &&
    !payOnDelivery.cashlessPaymentCompleted &&
    !!parseFloat(payOnDelivery.totalCashlessAmount);
  const pendingPayment = new RegExp(
    COD_PAYMENT_PENDING_STATUS.join('|'),
    'i',
  ).test(codPaymentStatus);
  const showLoading = disabled || pendingPayment;

  const handleCloseVersionUpdate = () => {
    setVersionUpdateVisible(false);
  };

  const handlePayment = async () => {
    scribeTrack.expressTracking.pay({
      trackingSource,
      orderIdDisplayed: orderId,
      isInApp: inGrabApp(),
      trackingPageType,
      amount: payOnDelivery.totalCashlessAmount,
    });

    const { result: enableRegularCashlessCODOnPax } = await getBoolValue({
      name: 'expressEnableRegularCashlessCODOnPax',
    });
    const showVersionUpdate = inGrabApp() && !enableRegularCashlessCODOnPax;
    if (showVersionUpdate) {
      setVersionUpdateVisible(true);
      return;
    }

    setDisabled(true);

    // stop auto fetching until the payment completed
    if (trackingInterval) {
      clearInterval(trackingInterval);
      dispatch(stopTrackingInterval());
    }

    await proceedToPay({ orderId });

    fetchData({
      type: FETCH_DATA_TYPE.FULL,
      dispatch,
      inApp,
      guid,
      searchParams,
      getAccessToken,
    });
    setDisabled(false);
  };

  if (!showButton) {
    return null;
  }

  return (
    <Fragment>
      <div className={styles.container}>
        {cashlessOnly && (
          <div className={styles.transaction}>
            <p data-testid="payment-total" className={styles.title}>
              {I18n.t('paymentFooter.totalToPay')}
            </p>
            <p data-testid="payment-amount" className={styles.total}>
              {formatPrice({
                amount: absAmount(payOnDelivery.totalCashlessAmount),
                symbol,
              })}
            </p>
          </div>
        )}

        {mixPayment && (
          <Fragment>
            <div className={styles.transaction}>
              <p data-testid="cash-action" className={styles.title}>
                {isNegativeAmount(payOnDelivery.totalCashAmount)
                  ? I18n.t('paymentFooter.collectCash')
                  : I18n.t('paymentFooter.payCash')}
              </p>
              <p data-testid="cash-amount" className={styles.total}>
                {formatPrice({
                  amount: absAmount(payOnDelivery.totalCashAmount),
                  symbol,
                })}
              </p>
            </div>
            <div className={styles.transaction}>
              <p data-testid="cashless-action" className={styles.title}>
                {payOnDelivery.cashlessPaymentCompleted
                  ? I18n.t('paymentFooter.paidCashless')
                  : I18n.t('paymentFooter.payCashless')}
              </p>

              <p data-testid="cashless-amount" className={styles.total}>
                {formatPrice({
                  amount: payOnDelivery.totalCashlessAmount,
                  symbol,
                })}
              </p>
            </div>
          </Fragment>
        )}

        <div className={styles.buttonContainer}>
          {showLoading ? (
            <Button
              data-testid="button-loading"
              loading
              shape="circle"
              size="large"
            />
          ) : (
            <Button
              data-testid="button-pay"
              type="primary"
              aria-label="proceed to pay"
              className={styles.paymentButton}
              disabled={disabled}
              onClick={handlePayment}
            >
              {I18n.t('paymentFooter.payButton')}
            </Button>
          )}
        </div>
      </div>
      <UpdateAppDrawer
        visible={versionUpdateVisible}
        handleClose={handleCloseVersionUpdate}
      />
    </Fragment>
  );
};

PayOnDeliveryPaymentFooter.propTypes = {
  status: PropTypes.string,
  codPaymentStatus: PropTypes.string,
  symbol: PropTypes.string,
  userType: PropTypes.shape({
    isReceiver: PropTypes.bool,
    isSender: PropTypes.bool,
  }),
  payOnDelivery: PropTypes.shape({
    items: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string,
        amount: PropTypes.string,
        paymentMethod: PropTypes.string,
      }),
    ),
    totalAmount: PropTypes.string,
    totalCashAmount: PropTypes.string,
    totalCashlessAmount: PropTypes.string,
    cashlessPaymentCompleted: PropTypes.bool,
  }),
};

PayOnDeliveryPaymentFooter.defaultProps = {
  status: '',
  codPaymentStatus: '',
  symbol: '',
  userType: {
    isSender: false,
    isReceiver: true,
  },
  payOnDelivery: {
    items: [],
    totalAmount: '',
    totalCashAmount: '',
    totalCashlessAmount: '',
    cashlessPaymentCompleted: false,
  },
};

export default PayOnDeliveryPaymentFooter;
